import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { SLICE_AXES } from '../lib/volume-slices';

const btn = 'flex-1 px-1 py-1 rounded text-[10px] cursor-pointer border';
const MODES = [['off', 'Off'], ['static', 'Static'], ['dynamic', 'Dynamic']];
const AXIS_NAMES = { x: 'X', y: 'Y', z: 'Z' };

/**
 * Slices section of the Volume tools (EarthImager 3D 3.8.6 / 3.8.7): Off | Static | Dynamic. Static
 * draws N evenly spaced slices normal to X, Y or Z (one slice has a position slider) or the
 * diagonals Y~X, Y~-X, Y~|X|; dynamic has three draggable slices toggled here or with the x, y, z keys.
 * `slices` is lib/volume-slices DEFAULT_SLICES-shaped state.
 */
export default function VolumeSliceControls({ hasModel, slices, setSlices, onView2D }) {
  const { C } = useTheme();
  if (!hasModel) return null;
  const patch = (p) => setSlices((s) => ({ ...s, ...p }));
  const patchDyn = (a, p) => setSlices((s) => ({ ...s, dyn: { ...s.dyn, [a]: { ...s.dyn[a], ...p } } }));
  const pressed = (on) => `${btn} ${on ? 'bg-accent-soft text-accent border-accent-soft' : 'border-transparent'}`;
  const off = (on) => (on ? undefined : { background: C.bg3, color: C.text2 });
  const straight = ['x', 'y', 'z'].includes(slices.axis);
  const single = slices.mode === 'static' && straight && slices.count === 1;
  const diag = slices.mode === 'static' && !straight;
  const can2d = slices.mode === 'static' && slices.axis !== 'abs' && (!straight || slices.count === 1);

  return (
    <div data-testid="slice-controls" className="mt-2">
      <div className="text-[10px] mb-1" style={{ color: C.text2 }}>Slices</div>
      <div className="flex gap-1 mb-1" role="group" aria-label="Slice mode">
        {MODES.map(([id, label]) => (
          <button key={id} type="button" data-testid={`slice-mode-${id}`} aria-pressed={slices.mode === id}
            onClick={() => patch({ mode: id })} className={pressed(slices.mode === id)} style={off(slices.mode === id)}>{label}</button>
        ))}
      </div>

      {slices.mode === 'static' && (
        <>
          <div className="flex flex-wrap gap-1 mb-1" role="group" aria-label="Slice axis">
            {SLICE_AXES.map(({ id, label }) => (
              <button key={id} type="button" data-testid={`slice-axis-${id}`} aria-pressed={slices.axis === id}
                title={['x', 'y', 'z'].includes(id) ? `Slices normal to ${label}` : `Vertical slice ${label}`}
                onClick={() => patch({ axis: id })} className={pressed(slices.axis === id)} style={off(slices.axis === id)}>{label}</button>
            ))}
          </div>
          {straight && (
            <>
              <div className="text-[10px]" style={{ color: C.text2 }}>Slices: {slices.count}</div>
              <input type="range" aria-label="Slice count" min={1} max={20} step={1} value={slices.count}
                onChange={(e) => patch({ count: parseInt(e.target.value, 10) })} className="w-full accent-cyan-400" />
            </>
          )}
          {single && (
            <>
              <div className="text-[10px]" style={{ color: C.text2 }}>Position: {(slices.pos * 100).toFixed(0)}%</div>
              <input type="range" aria-label="Slice position" min={0} max={1} step={0.01} value={slices.pos}
                onChange={(e) => patch({ pos: parseFloat(e.target.value) })} className="w-full accent-cyan-400" />
            </>
          )}
          {diag && <div className="text-[10px] mb-1" style={{ color: C.text3 }}>Vertical slice along the grid diagonal.</div>}
          {can2d && (
            <button type="button" data-testid="slice-2d" onClick={onView2D} className={`${btn} w-full mb-1`}
              style={{ background: C.bg3, color: C.text1, borderColor: C.border }}>2D view</button>
          )}
        </>
      )}

      {slices.mode === 'dynamic' && (
        <div data-testid="slice-dynamic">
          {Object.keys(AXIS_NAMES).map((a) => (
            <div key={a} className="mb-1">
              <label className="flex items-center gap-2 text-[10px] cursor-pointer" style={{ color: C.text1 }}>
                <input type="checkbox" data-testid={`slice-dyn-${a}`} checked={slices.dyn[a].on}
                  onChange={(e) => patchDyn(a, { on: e.target.checked })} className="accent-cyan-400" />
                {AXIS_NAMES[a]} slice <span style={{ color: C.text3 }}>(key {a})</span>
              </label>
              {slices.dyn[a].on && (
                <input type="range" aria-label={`${AXIS_NAMES[a]} slice position`} min={0} max={1} step={0.01} value={slices.dyn[a].pos}
                  onChange={(e) => patchDyn(a, { pos: parseFloat(e.target.value) })} className="w-full accent-cyan-400" />
              )}
            </div>
          ))}
          <div className="text-[10px]" style={{ color: C.text3 }}>Drag a slice in the scene to move it; click it for the block and resistivity.</div>
        </div>
      )}

      {slices.mode !== 'off' && (
        <label className="flex items-center gap-2 text-[10px] mt-1 cursor-pointer" style={{ color: C.text1 }}>
          <input type="checkbox" data-testid="slice-hide-blocks" checked={slices.hideBlocks}
            onChange={(e) => patch({ hideBlocks: e.target.checked })} className="accent-cyan-400" />
          Hide blocks while slicing
        </label>
      )}
    </div>
  );
}
